import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuBell } from 'react-icons/lu';

import { NOTIFICATIONS_IN_DROPDOWN } from '../../constants';
import useNotifications from '../../hooks/useNotifications';
import useDismissable from '../../hooks/useDismissable';
import NotificationItem from './NotificationItem';

/**
 * The bell in the header and the dropdown behind it — Figma 16370:192967.
 *
 * 400px wide, 16px round, a hairline of border/light-neutral and one soft
 * shadow. A header with "الإشعارات" at the inline start and "مسح الكل" at the
 * end, the rows ruled off from each other, and "تحميل المزيد" in a footer.
 *
 * The dot is drawn from the store's own unread counts — `notNormalSeen` and
 * `notImportantSeen` — so nothing here can disagree with the rest of the app
 * about whether there is something new.
 *
 * "مسح الكل" marks everything read rather than deleting anything, because the
 * API has a read endpoint and no clear endpoint. The reader sees what they
 * expect — the dot goes, the tinted rows go flat — and nothing is lost, which
 * is arguably the better trade anyway. Recorded in
 * `docs/student-backend-gaps.md`.
 *
 * "تحميل المزيد" reveals five more from the list already in the store. It is
 * not a request: `/user/notifications` returns everything in one go and takes
 * no paging parameters, so there is no second page to fetch. The button stops
 * being drawn once the whole list is on screen.
 */
const NotificationsBell = ({ isOpen, onToggle, onClose }) => {
    const { t } = useTranslation();
    const { items, unreadCount, markAllRead } = useNotifications();
    const { triggerRef, panelRef } = useDismissable({ isOpen, onClose });
    const [visible, setVisible] = useState(NOTIFICATIONS_IN_DROPDOWN);

    // Reopening the panel should start at the top again, not wherever the
    // reader had unrolled it to last time.
    useEffect(() => {
        if (!isOpen) setVisible(NOTIFICATIONS_IN_DROPDOWN);
    }, [isOpen]);

    const shown = items.slice(0, visible);
    const hasMore = items.length > visible;

    return (
        <div className="tw-relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-label={
                    unreadCount
                        ? t('v2.student.notifications.withCount', { count: unreadCount })
                        : t('v2.student.notifications.title')
                }
                className={
                    'tw-relative tw-flex tw-size-[36px] tw-items-center tw-justify-center ' +
                    'tw-rounded-full tw-text-v2-student-name hover:tw-bg-v2-neutral-50 ' +
                    'tw-transition-colors focus-visible:tw-outline-none ' +
                    'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                }
            >
                <LuBell size={20} aria-hidden />

                {unreadCount > 0 && (
                    <span
                        aria-hidden
                        className={
                            'tw-absolute tw-top-[6px] tw-size-[8px] tw-rounded-full ' +
                            'tw-bg-v2-student-dot tw-ring-2 tw-ring-white'
                        }
                        style={{ insetInlineEnd: '6px' }}
                    />
                )}
            </button>

            {isOpen && (
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-label={t('v2.student.notifications.title')}
                    className={
                        'tw-absolute tw-top-[calc(100%+10px)] tw-z-50 tw-flex tw-max-h-[80vh] ' +
                        'tw-w-[calc(100vw-32px)] sm:tw-w-[400px] tw-flex-col tw-overflow-hidden ' +
                        'tw-rounded-[16px] tw-bg-white tw-border tw-border-solid ' +
                        'tw-border-v2-line tw-shadow-v2-dropdown'
                    }
                    style={{ insetInlineEnd: 0 }}
                >
                    <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-v2-line tw-px-[20px] tw-py-[16px]">
                        <h2 className="tw-m-0 tw-text-[16px] tw-font-bold tw-leading-[1.5] tw-text-v2-neutral-900">
                            {t('v2.student.notifications.title')}
                        </h2>

                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="tw-rounded tw-text-[13px] tw-font-bold tw-leading-[1.6] tw-text-v2-brand hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand"
                            >
                                {t('v2.student.notifications.clearAll')}
                            </button>
                        )}
                    </div>

                    <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
                        {shown.length ? (
                            shown.map((n, index) => (
                                <div
                                    key={n.notificationId ?? index}
                                    className="tw-border-0 tw-border-b tw-border-solid tw-border-v2-line last:tw-border-b-0"
                                >
                                    <NotificationItem notification={n} />
                                </div>
                            ))
                        ) : (
                            <p className="tw-m-0 tw-px-[20px] tw-py-[32px] tw-text-center tw-text-[14px] tw-text-v2-text-excerpt">
                                {t('v2.student.notifications.empty')}
                            </p>
                        )}
                    </div>

                    {hasMore && (
                        <button
                            type="button"
                            onClick={() => setVisible((n) => n + NOTIFICATIONS_IN_DROPDOWN)}
                            className={
                                'tw-shrink-0 tw-border-0 tw-border-t tw-border-solid tw-border-v2-line ' +
                                'tw-bg-white tw-p-[16px] tw-text-[14px] tw-font-bold tw-leading-[1.6] tw-text-v2-brand ' +
                                'hover:tw-bg-v2-neutral-50 tw-transition-colors focus-visible:tw-outline-none ' +
                                'focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-v2-brand'
                            }
                        >
                            {t('v2.student.notifications.loadMore')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
